import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';

export type InsuranceTypeLookup = {
  id: string;
  name: string;
  code: string;
  icon?: string;
};

export type InsuranceGroupLookup = {
  id: string;
  name: string;
  code: string;
  insurance_type: string;
};

export type InsuranceSubgroupLookup = {
  id: string;
  name: string;
  code: string;
  group: string;
};

export type PolicyTypeLookup = {
  id: string;
  name: string;
  code: string;
  insurance_type: string;
};

export type ProductLookup = {
  id: string;
  name: string;
  // The insurer-mapping's own name (falls back to `name` when the request
  // carries no `insurer`, or the insurer never mapped this product under a
  // distinct name). Always present — use this for display, not `name`.
  mapped_name: string;
  code: string;
  insurer: string;
  insurer_name: string;
  insurance_type?: string | null;
  // Read straight off the catalog Product, not resolved through the
  // insurer's mapping — InsurancePolicy has no group/subgroup field of its
  // own. Identical for every insurer offering this same product.
  group?: string | null;
  subgroup?: string | null;
  policy_type?: string | null;
};

export function useInsuranceTypesLookup(options?: { enabled?: boolean }) {
  return useQuery<InsuranceTypeLookup[]>({
    queryKey: ['insurance-types', 'lookup'],
    queryFn: async () => {
      const response = await api.get('/products/types/lookup/');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

export function useInsuranceGroupsLookup(insuranceTypeId?: string) {
  return useQuery<InsuranceGroupLookup[]>({
    queryKey: ['insurance-groups', 'lookup', insuranceTypeId],
    queryFn: async () => {
      const response = await api.get('/products/groups/lookup/', {
        params: insuranceTypeId ? { insurance_type: insuranceTypeId } : {},
      });
      return response.data;
    },
    enabled: !!insuranceTypeId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllInsuranceGroupsLookup() {
  return useQuery<InsuranceGroupLookup[]>({
    queryKey: ['insurance-groups', 'lookup', 'all'],
    queryFn: async () => {
      const response = await api.get('/products/groups/lookup/');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useInsuranceSubgroupsLookup(groupId?: string) {
  return useQuery<InsuranceSubgroupLookup[]>({
    queryKey: ['insurance-subgroups', 'lookup', groupId],
    queryFn: async () => {
      const response = await api.get('/products/subgroups/lookup/', {
        params: groupId ? { group: groupId } : {},
      });
      return response.data;
    },
    enabled: !!groupId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Active policy types, optionally scoped to an insurance type (FIX 10/11).
 * Hits the audit-literal alias `/policy-types/` with the viewset's
 * `insurance_type` / `is_active` filterset fields.
 */
export function usePolicyTypesLookup(insuranceTypeId?: string, opts?: { enabled?: boolean }) {
  return useQuery<PolicyTypeLookup[]>({
    queryKey: ['policy-types', 'lookup', insuranceTypeId ?? 'all'],
    queryFn: async () => {
      const response = await api.get('/policy-types/', {
        params: {
          is_active: true,
          page_size: 100,
          ...(insuranceTypeId ? { insurance_type: insuranceTypeId } : {}),
        },
      });
      // Endpoint is paginated; lookups only need the rows.
      return response.data?.results ?? response.data ?? [];
    },
    enabled: opts?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

export function useProductsLookup(
  params?: {
    insurance_type?: string;
    insurer?: string;
    group?: string;
    subgroup?: string;
    /** B5: the resolved `PolicyType` id for a Product Variant selection --
     *  see `useProductVariantPolicyType` below. */
    policy_type?: string;
    /** M-03: `'wildcard'` makes a product with NO subgroup / policy_type
     *  match any selected subgroup / policy_type (insurance_type and group
     *  stay strict). Omit it for strict equality on every dimension. */
    match?: 'wildcard';
  },
  opts?: { enabled?: boolean }
) {
  return useQuery<ProductLookup[]>({
    queryKey: ['products', 'lookup', params],
    queryFn: async () => {
      const response = await api.get('/products/products/lookup/', { params });
      return response.data;
    },
    // Callers that narrow a form step want the list to stay empty until a
    // type/insurer is chosen. The Policies filter bar (queue #226) needs the
    // opposite -- the full catalogue with no prior selection -- so it opts in
    // explicitly rather than this default being flipped for everyone.
    enabled: opts?.enabled ?? !!(params?.insurance_type || params?.insurer),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * B14 -- the quote wizard's coarse `vehicle_category`
 * (`two_wheeler` | `private_car` | `commercial_vehicle`, `MotorQuoteFormData`'s
 * spelling; saved onto `QuoteRequest.additional_data.vehicle_data.vehicle_type`)
 * mapped to the matching `Product.group`, so the Premium Selection stage can
 * pass `group` to `useProductsLookup` and show only that vehicle type's
 * products.
 */
export const VEHICLE_CATEGORY_GROUP_NAMES: Record<string, string> = {
  two_wheeler: 'Two Wheeler',
  private_car: 'Private Car',
  commercial_vehicle: 'Commercial Vehicle',
};

export interface VehicleCategoryGroup {
  /** The resolved `InsuranceGroup` id, once found. Undefined while loading,
   *  on no `vehicleCategory`, or if resolution failed. */
  groupId?: string;
  /** Display label for messaging -- "Two Wheeler", not the raw category code. */
  groupLabel?: string;
  isLoading: boolean;
  isError: boolean;
  /** The groups lookup answered, with no error, but nothing matched this
   *  category's name -- a catalog data problem, not a network failure. The
   *  caller should say so rather than rendering an empty dropdown as if the
   *  insurer simply had nothing. */
  notConfigured: boolean;
}

/**
 * Resolves `vehicleCategory` to an `InsuranceGroup` by NAME, not by
 * `InsuranceGroup.code`. Verified against production data (2026-09-15): the
 * three motor groups' codes are inconsistent junk left over from earlier
 * imports -- `TWO_WHELLER` (misspelt), `pc`, `commercial_vehicle` -- while
 * every group's English name is unique across the WHOLE catalog ('Two
 * Wheeler', 'Private Car' and 'Commercial Vehicle' each name exactly one
 * active group, motor or otherwise). Matching on a code a country admin
 * cannot see or fix would have broken again the next time one diverged;
 * matching on the label they actually typed does not depend on that code
 * ever being tidied up.
 */
export function useVehicleCategoryGroup(vehicleCategory?: string): VehicleCategoryGroup {
  const { data: groups, isLoading, isError } = useAllInsuranceGroupsLookup();
  const groupLabel = vehicleCategory ? VEHICLE_CATEGORY_GROUP_NAMES[vehicleCategory] : undefined;

  const group = groupLabel
    ? (groups ?? []).find((g) => g.name.trim().toLowerCase() === groupLabel.toLowerCase())
    : undefined;

  return {
    groupId: group?.id,
    groupLabel,
    isLoading: !!vehicleCategory && isLoading,
    isError: !!vehicleCategory && isError,
    notConfigured: !!vehicleCategory && !isLoading && !isError && !group,
  };
}

/**
 * B5 -- the quote wizard's Product Variant step (`MotorQuoteFormData.
 * product_variant`, `pages/quotes/wizard/schema.ts`) mapped to the matching
 * `PolicyType` by NAME, the same discipline `useVehicleCategoryGroup` above
 * uses for vehicle categories and for the same reason: `PolicyType.code` is
 * a slug generated from the name at seed time
 * (`apps/products/migrations/0008_seed_policy_types.py`,
 * `code = name.upper().replace(" ", "_")`), not a stable identifier a country
 * admin is shown or asked to keep in sync, so matching the label they can
 * actually see survives a rename that would break a match on `code`.
 *
 * The three wizard values line up with three of the four seeded Motor
 * PolicyType rows ("Comprehensive", "Standalone OD", "Standalone TP" --
 * the fourth, "Bundled", has no wizard equivalent and is simply never
 * selected by this filter, on any variant, which is correct: a bundled OD+TP
 * product is neither "Comprehensive" nor "Standalone" alone). If a country
 * admin renames or removes one of these three, `notConfigured` is true and
 * the caller must say so rather than silently showing every variant's
 * products, or matching the wrong one.
 */
export const PRODUCT_VARIANT_POLICY_TYPE_NAMES: Record<string, string> = {
  comprehensive: 'Comprehensive',
  tp_only: 'Standalone TP',
  saod: 'Standalone OD',
};

export interface ProductVariantPolicyType {
  /** The resolved `PolicyType` id, once found. Undefined while loading, on
   *  no `productVariant`, or if resolution failed. */
  policyTypeId?: string;
  /** Display label for messaging -- "Standalone OD", not the raw enum value. */
  policyTypeLabel?: string;
  isLoading: boolean;
  isError: boolean;
  /** The policy-types lookup answered, with no error, but no `PolicyType`
   *  matched this variant's name -- a catalog data problem (a renamed or
   *  removed master row), distinct from a failed request. */
  notConfigured: boolean;
}

export function useProductVariantPolicyType(
  insuranceTypeId?: string,
  productVariant?: string
): ProductVariantPolicyType {
  const { data: policyTypes, isLoading, isError } = usePolicyTypesLookup(insuranceTypeId, {
    enabled: !!insuranceTypeId && !!productVariant,
  });
  const policyTypeLabel = productVariant ? PRODUCT_VARIANT_POLICY_TYPE_NAMES[productVariant] : undefined;

  const policyType = policyTypeLabel
    ? (policyTypes ?? []).find((p) => p.name.trim().toLowerCase() === policyTypeLabel.toLowerCase())
    : undefined;

  return {
    policyTypeId: policyType?.id,
    policyTypeLabel,
    isLoading: !!productVariant && isLoading,
    isError: !!productVariant && isError,
    notConfigured: !!productVariant && !isLoading && !isError && !policyType,
  };
}
